import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Message, Role, Coordinates, Place, Location, RouteSuggestion, SavedJourney, OpeningHours } from './types';
import { sendChatMessage, getOrCreateSessionId, getRoute, recalculateRoute, textToSpeech } from './services/routexApi';
import { ChatMessage, LoadingIndicator, AgentStatusBar } from './components/ChatMessage';
import { FilterChips, DEFAULT_FILTERS } from './components/FilterChips';
import { BottomNav, DEFAULT_TABS } from './components/BottomNav';
import { GoogleMap } from './components/GoogleMap';
import { LocationInput } from './components/LocationInput';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage } from './components/LoginPage';
import { ProfileSection } from './components/ProfileSection';
import { WelcomePage, TripType } from './components/WelcomePage';
import { TripConfiguration, TripConfig } from './components/TripConfiguration';
import { PlaceDetailsModal } from './components/PlaceDetailsModal';
import { getOrInitializeAccount, decrementResponseCount, canUseApp, getAccountData, updateAccountType, getEffectiveResponseCount } from './utils/accountUtils';
import { saveSavedJourney } from './utils/savedJourneys';

const WELCOME_MESSAGE = `👋 Welcome to RouteX!

I'm your AI travel assistant. To get started, please set your start and destination addresses using the location input bars at the top of the map.

Once you've entered both locations, I'll help you find the best route with interesting stops along the way.

You can also ask me:
• "Find coffee shops along my route"
• "I want to visit museums on my way"
• Or any other questions about your journey!`;

// Sortable stop row for Directions tab (uses @dnd-kit). Drag from handle; click content to open place details.
function SortableStopRow({
  id,
  place,
  displayIndex,
  placeType,
  etaText,
  estStayMinutes,
  estStayText,
  onStayMinutesChange,
  onPlaceClick,
}: {
  id: string;
  place: Place;
  displayIndex: number;
  placeType: string;
  etaText?: string;
  estStayMinutes?: number;
  estStayText?: string;
  onStayMinutesChange?: (minutes: number) => void;
  onPlaceClick?: (place: Place) => void;
}) {
  const [isEditingStay, setIsEditingStay] = useState(false);
  const [stayDraft, setStayDraft] = useState<string>('');

  useEffect(() => {
    if (!isEditingStay) return;
    if (estStayMinutes != null && Number.isFinite(estStayMinutes)) {
      setStayDraft(String(estStayMinutes));
      return;
    }
    if (estStayText) {
      const m = estStayText.match(/(\d+)/);
      setStayDraft(m ? m[1] : '');
      return;
    }
    setStayDraft('');
  }, [isEditingStay, estStayMinutes, estStayText]);

  const bookingStorageKey = `routex.bookingStatus.${place.place_id || id}`;
  const [bookingStatus, setBookingStatus] = useState<
    'idle' | 'loading' | 'success' | 'failed'
  >(() => {
    try {
      const saved = window.localStorage.getItem(bookingStorageKey);
      if (saved === 'success' || saved === 'failed' || saved === 'idle') return saved;
      return 'idle';
    } catch {
      return 'idle';
    }
  });

  // Persist status (but don't persist transient loading state).
  useEffect(() => {
    if (bookingStatus === 'loading') return;
    try {
      window.localStorage.setItem(bookingStorageKey, bookingStatus);
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, [bookingStatus, bookingStorageKey]);

  const handleAutomaticBooking = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (bookingStatus === 'loading') return;

    // Clicking Booked! cancels/undoes the booking.
    if (bookingStatus === 'success') {
      setBookingStatus('idle');
      return;
    }

    try {
      setBookingStatus('loading');

      // Frontend-only "automatic booking" simulation.
      // If/when a real booking endpoint exists, replace this with an API call.
      await new Promise((resolve) => setTimeout(resolve, 1200));

      // Bias toward success to feel realistic, but still allow failure state.
      const ok = Math.random() < 0.85;
      setBookingStatus(ok ? 'success' : 'failed');
    } catch {
      setBookingStatus('failed');
    }
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl border p-4 select-none ${
        isDragging ? 'opacity-50 border-blue-400 shadow-lg z-10' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className="material-symbols-outlined text-gray-400 flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing"
          aria-hidden
          {...attributes}
          {...listeners}
        >
          drag_indicator
        </span>
        <span className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 text-xs font-medium mt-0.5 min-w-[2.25rem] justify-center" title="Rating">
          <span className="material-symbols-outlined text-amber-600 text-sm">star</span>
          {place.rating != null ? place.rating.toFixed(1) : '—'}
        </span>
        <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
          {displayIndex + 1}
        </div>
        <button
          type="button"
          onClick={() => onPlaceClick?.(place)}
          className="flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity rounded-lg -m-1 p-1"
        >
          <p className="text-gray-900 font-medium">{place.name}</p>
          {place.formatted_address && (
            <p className="text-sm text-gray-500 mt-1 truncate">{place.formatted_address}</p>
          )}
          <p className="text-sm text-gray-400 mt-1">({placeType})</p>
        </button>

        <div className="flex-shrink-0 mt-0.5 text-right">
          <button
            type="button"
            onClick={handleAutomaticBooking}
            disabled={bookingStatus === 'loading'}
            className={[
              'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
              'disabled:opacity-80 disabled:cursor-not-allowed',
              bookingStatus === 'idle'
                ? 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                : '',
              bookingStatus === 'loading'
                ? 'bg-gray-100 text-gray-700 border-gray-200'
                : '',
              bookingStatus === 'success'
                ? 'bg-green-100 text-green-700 border-green-200'
                : '',
              bookingStatus === 'failed'
                ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200'
                : '',
            ].join(' ')}
          >
            <span className="inline-flex items-center gap-2">
              {bookingStatus === 'loading' && (
                <span
                  className="inline-block h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin"
                  aria-label="Processing"
                />
              )}
              {bookingStatus === 'idle' && 'Automatic Booking'}
              {bookingStatus === 'loading' && 'Processing...'}
              {bookingStatus === 'success' && 'Booked!'}
              {bookingStatus === 'failed' && 'Failed!'}
            </span>
          </button>

          {(etaText || estStayText) && (
            <div className="mt-1">
              {etaText && (
                <div className="text-[11px] font-semibold text-gray-700">
                  ETA {etaText}
                </div>
              )}
              {estStayText && (
                <div className="text-[11px] text-gray-500">
                  <span className="mr-1">Estimated stay</span>
                  {!isEditingStay ? (
                    <button
                      type="button"
                      className="underline decoration-dotted underline-offset-2 hover:text-gray-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingStay(true);
                      }}
                      title="Click to edit stay duration"
                    >
                      {estStayText}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        step={5}
                        value={stayDraft}
                        onChange={(e) => setStayDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = Number(stayDraft);
                            if (Number.isFinite(v)) onStayMinutesChange?.(Math.max(0, v));
                            setIsEditingStay(false);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setIsEditingStay(false);
                          }
                        }}
                        onBlur={() => {
                          const v = Number(stayDraft);
                          if (Number.isFinite(v)) onStayMinutesChange?.(Math.max(0, v));
                          setIsEditingStay(false);
                        }}
                        className="w-14 px-1 py-0.5 rounded border border-gray-200 text-[11px] text-gray-700 bg-white"
                        autoFocus
                      />
                      <span className="text-[11px] text-gray-500">min</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main App Content (requires authentication)
const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  const [selectedTripType, setSelectedTripType] = useState<TripType>(null);
  const [tripConfig, setTripConfig] = useState<TripConfig | null>(null);
  const [showConfigEdit, setShowConfigEdit] = useState(false);

  // Show loading screen while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
            <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              route
            </span>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // Show welcome page if authenticated but no trip type selected
  if (!selectedTripType) {
    return (
      <WelcomePage
        onSelectTripType={(tripType) => {
          // Only set trip type if it's not "coming soon"
          if (tripType === 'single-day') {
            setSelectedTripType(tripType);
          }
        }}
      />
    );
  }

  // Show trip configuration in edit mode if requested
  if (showConfigEdit && selectedTripType === 'single-day') {
    return (
      <TripConfiguration
        onComplete={(config) => {
          setTripConfig(config);
          setShowConfigEdit(false);
        }}
        onBack={() => setShowConfigEdit(false)}
        initialConfig={tripConfig || undefined}
      />
    );
  }

  // Show trip configuration page if single-day is selected but config not completed
  if (selectedTripType === 'single-day' && !tripConfig) {
    return (
      <TripConfiguration
        onComplete={(config) => {
          setTripConfig(config);
        }}
        onBack={() => setSelectedTripType(null)}
      />
    );
  }

  return (
    <MainApp
      tripType={selectedTripType}
      tripConfig={tripConfig}
      onBackToWelcome={() => {
        setSelectedTripType(null);
        setTripConfig(null);
      }}
      onEditConfig={() => setShowConfigEdit(true)}
    />
  );
};

// Main App Component
interface MainAppProps {
  tripType: TripType;
  tripConfig: TripConfig | null;
  onBackToWelcome: () => void;
  onEditConfig: () => void;
}

const MainApp: React.FC<MainAppProps> = ({ tripType, tripConfig, onBackToWelcome, onEditConfig }) => {
  // tripType will be used later to customize behavior based on trip type
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatusIndex, setLoadingStatusIndex] = useState(0);
  const LOADING_STATUS_MESSAGES = ['Thinking...', 'Searching...', 'Analysing...', 'Routing...'];
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [location, setLocation] = useState<Coordinates | undefined>(undefined);
  const [activeTab, setActiveTab] = useState('map'); // Default to map tab now
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sessionId] = useState(() => getOrCreateSessionId());
  const [startLocation, setStartLocation] = useState('');
  const [endLocation, setEndLocation] = useState('');
  const [startLocationCoords, setStartLocationCoords] = useState<Location | undefined>(undefined);
  const [endLocationCoords, setEndLocationCoords] = useState<Location | undefined>(undefined);
  const [isSelectingLocation, setIsSelectingLocation] = useState<'start' | 'end' | null>(null);
  const [suggestedPlaces, setSuggestedPlaces] = useState<Place[]>([]);
  const [mapCenter, setMapCenter] = useState<Location | undefined>(undefined);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [isPlaceDetailsOpen, setIsPlaceDetailsOpen] = useState(false);
  const [chatButtonPosition, setChatButtonPosition] = useState({ x: 16, y: 96 }); // Default: left-4 bottom-24 (16px from left, 96px from bottom)
  const [isDraggingChatButton, setIsDraggingChatButton] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [routePolyline, setRoutePolyline] = useState<string | undefined>(undefined);
  const [correctedRoutePolyline, setCorrectedRoutePolyline] = useState<string | undefined>(undefined);
  const [routeSuggestions, setRouteSuggestions] = useState<RouteSuggestion[]>([]);
  const [fastestRoute, setFastestRoute] = useState<RouteSuggestion | null>(null); // Store fastest route for comparison
  const [activeRouteSuggestion, setActiveRouteSuggestion] = useState<number>(0);
  const [showGoConfirmDialog, setShowGoConfirmDialog] = useState(false);
  const [showBookingPlaceholder, setShowBookingPlaceholder] = useState(false);
  const [showNavigationAppDialog, setShowNavigationAppDialog] = useState(false);
  const [appleLegPoints, setAppleLegPoints] = useState<string[]>([]);
  const [currentAppleLegIndex, setCurrentAppleLegIndex] = useState<number>(0); // leg i => points[i] -> points[i+1]
  const [savedJourneyMessage, setSavedJourneyMessage] = useState<string | null>(null);
  const [openingHoursByPlaceId, setOpeningHoursByPlaceId] = useState<Record<string, OpeningHours | null>>({});
  const [directionsStartTimeMode, setDirectionsStartTimeMode] = useState<'now' | 'custom'>(() => {
    try {
      const v = window.localStorage.getItem('routex.directionsStartTimeMode');
      return v === 'custom' ? 'custom' : 'now';
    } catch {
      return 'now';
    }
  });
  const [directionsStartDateTime, setDirectionsStartDateTime] = useState<string>(() => {
    try {
      return window.localStorage.getItem('routex.directionsStartDateTime') || '';
    } catch {
      return '';
    }
  }); // from <input type="datetime-local">
  const [stayOverridesByStopId, setStayOverridesByStopId] = useState<Record<string, number>>(() => {
    try {
      const raw = window.localStorage.getItem('routex.stayOverridesByStopId');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(parsed)) {
        const n = Number(v);
        if (k && Number.isFinite(n) && n >= 0) out[k] = n;
      }
      return out;
    } catch {
      return {};
    }
  });
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const requestedOpeningHoursRef = useRef<Set<string>>(new Set());
  const activeRouteSuggestionRef = useRef<number>(0);
  const reorderedStopsRef = useRef<Place[] | null>(null);

  // Persist Directions booking inputs
  useEffect(() => {
    try {
      window.localStorage.setItem('routex.directionsStartTimeMode', directionsStartTimeMode);
      window.localStorage.setItem('routex.directionsStartDateTime', directionsStartDateTime);
    } catch {
      // ignore
    }
  }, [directionsStartTimeMode, directionsStartDateTime]);

  // Persist per-stop stay overrides
  useEffect(() => {
    try {
      window.localStorage.setItem(
        'routex.stayOverridesByStopId',
        JSON.stringify(stayOverridesByStopId)
      );
    } catch {
      // ignore
    }
  }, [stayOverridesByStopId]);

  // Sensors for @dnd-kit (Directions tab stop reordering)
  const directionsSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Keep ref in sync with state
  useEffect(() => {
    activeRouteSuggestionRef.current = activeRouteSuggestion;
  }, [activeRouteSuggestion]);

  // Correct/refresh route polyline on frontend using Google Directions to avoid occasional mismatches.
  // We derive the polyline from Google's returned route geometry (overview_path/legs),
  // so the yellow line always follows start -> ordered stops -> destination.
  useEffect(() => {
    let cancelled = false;

    const extractEncodedPolylineFromRoute = (route: any): string => {
      // Preferred source in JS SDK: overview_path (array of LatLng)
      const overviewPath = route?.overview_path;
      if (
        overviewPath &&
        Array.isArray(overviewPath) &&
        overviewPath.length > 1 &&
        window.google?.maps?.geometry?.encoding
      ) {
        try {
          return window.google.maps.geometry.encoding.encodePath(overviewPath);
        } catch {
          // fall through
        }
      }

      // Secondary source: overview_polyline object variants
      const overviewPolyline = route?.overview_polyline;
      const encodedFromOverview =
        overviewPolyline?.encoded_path ||
        overviewPolyline?.encodedPath ||
        overviewPolyline?.points ||
        '';
      if (encodedFromOverview) {
        return encodedFromOverview;
      }

      // Fallback: stitch path from legs/steps
      if (route?.legs && window.google?.maps?.geometry?.encoding) {
        try {
          const stitchedPath: google.maps.LatLng[] = [];
          route.legs.forEach((leg: any) => {
            (leg?.steps || []).forEach((step: any) => {
              if (step?.path && Array.isArray(step.path) && step.path.length > 0) {
                stitchedPath.push(...step.path);
              }
            });
          });
          if (stitchedPath.length > 1) {
            return window.google.maps.geometry.encoding.encodePath(stitchedPath);
          }
        } catch {
          // ignore
        }
      }

      return '';
    };

    const syncCorrectRoutePolyline = async () => {
      if (!startLocationCoords || !endLocationCoords) {
        setCorrectedRoutePolyline(undefined);
        return;
      }
      if (!window.google?.maps?.DirectionsService || !window.google?.maps?.TravelMode) {
        setCorrectedRoutePolyline(undefined);
        return;
      }

      try {
        // Clear stale corrected polyline so we don't keep showing an old route.
        setCorrectedRoutePolyline(undefined);

        const directionsService = new window.google.maps.DirectionsService();
        const mode = tripConfig?.travelMode || 'DRIVING';
        const waypoints = suggestedPlaces.map((p) => ({
          location: new window.google.maps.LatLng(p.location.lat, p.location.lng),
          stopover: true,
        }));

        directionsService.route(
          {
            origin: new window.google.maps.LatLng(
              startLocationCoords.lat,
              startLocationCoords.lng
            ),
            destination: new window.google.maps.LatLng(
              endLocationCoords.lat,
              endLocationCoords.lng
            ),
            waypoints,
            optimizeWaypoints: false,
            travelMode: window.google.maps.TravelMode[mode],
          },
          (result, status) => {
            if (cancelled) return;
            if (status === window.google.maps.DirectionsStatus.OK && result?.routes?.[0]) {
              const encoded = extractEncodedPolylineFromRoute(result.routes[0]);
              if (encoded) {
                setCorrectedRoutePolyline(encoded);
                return;
              }
            }
            // Leave undefined if correction fails (prefer no line over wrong line).
            setCorrectedRoutePolyline(undefined);
          }
        );
      } catch {
        setCorrectedRoutePolyline(undefined);
      }
    };

    syncCorrectRoutePolyline();
    return () => {
      cancelled = true;
    };
  }, [startLocationCoords, endLocationCoords, suggestedPlaces, tripConfig?.travelMode]);

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  // Fetch opening hours for places in Directions tab (Google Places API)
  useEffect(() => {
    if (activeTab !== 'route' || !suggestedPlaces.length || !window.google?.maps?.places) return;
    if (!placesServiceRef.current) {
      const mapDiv = document.createElement('div');
      const map = new window.google.maps.Map(mapDiv);
      placesServiceRef.current = new window.google.maps.places.PlacesService(map);
    }
    const service = placesServiceRef.current;
    suggestedPlaces.forEach((place) => {
      if (!place.place_id || place.place_id.startsWith('stop-')) return;
      if (place.place_id in openingHoursByPlaceId || requestedOpeningHoursRef.current.has(place.place_id)) return;
      requestedOpeningHoursRef.current.add(place.place_id);
      service.getDetails(
        { placeId: place.place_id, fields: ['opening_hours'] },
        (result: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !result) {
            setOpeningHoursByPlaceId((prev) => ({ ...prev, [place.place_id]: null }));
            return;
          }
          const oh = result.opening_hours;
          if (!oh) {
            setOpeningHoursByPlaceId((prev) => ({ ...prev, [place.place_id]: null }));
            return;
          }
          setOpeningHoursByPlaceId((prev) => ({
            ...prev,
            [place.place_id]: { open_now: oh.open_now, weekday_text: oh.weekday_text ?? undefined },
          }));
        }
      );
    });
  }, [activeTab, suggestedPlaces, openingHoursByPlaceId]);

  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [chatSwipeStartY, setChatSwipeStartY] = useState<number>(0);
  const [isLocationPanelCollapsed, setIsLocationPanelCollapsed] = useState(false);

  const filterPlanRouteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterSendMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedFiltersRef = useRef<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const endLocationInputRef = useRef<HTMLInputElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const chatButtonRef = useRef<HTMLButtonElement>(null);
  const isDraggingChatButtonRef = useRef(false);

  // Voice input and TTS controls
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false); // Ref to track mute state for event listeners
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isButtonHeldRef = useRef<boolean>(false); // Track if voice button is being held down
  const accumulatedTranscriptRef = useRef<string>(''); // Accumulate final transcript across continuous recognition events
  const isMessageSentRef = useRef<boolean>(false); // Track if a message was just sent to prevent onresult from restoring text
  const lastSpokenMessageIdRef = useRef<string>(''); // Track last spoken message ID
  const ttsGenerationAbortRef = useRef<AbortController | null>(null); // Track TTS generation to cancel if needed
  const isGeneratingTTSRef = useRef<boolean>(false); // Track if TTS is currently being generated

  // Ensure map tab is active when MainApp mounts (especially after configuration)
  useEffect(() => {
    setActiveTab('map');
  }, []);

  // Check account status periodically
  useEffect(() => {
    if (!user?.uid) return;

    const checkAccountStatus = () => {
      const accountData = getAccountData(user.uid!);
      if (accountData && accountData.accountType === 'Free' && getEffectiveResponseCount(user.uid!) === 0) {
        setShowUpgradePrompt(true);
      }
    };

    checkAccountStatus();
    const interval = setInterval(checkAccountStatus, 1000); // Check every second

    return () => clearInterval(interval);
  }, [user?.uid]);

  // Sync mute ref with state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          // Update map center with user's location
          setMapCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.warn("Location access denied:", error);
        }
      );
    }
  }, []);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Scroll to bottom when composing (so user can read what they're typing)
  useEffect(() => {
    if (inputText.trim()) scrollToBottom();
  }, [inputText, scrollToBottom]);

  // Cycle loading status: Thinking → Searching → Analysing → Routing, then stay at Routing
  useEffect(() => {
    if (!isLoading) {
      setLoadingStatusIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingStatusIndex((i) => (i >= 3 ? 3 : i + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isChatOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isChatOpen]);

  // Helper function to speak assistant messages using TTS
  const speakMessage = useCallback((text: string, language?: string, abortSignal?: AbortSignal) => {
    // Don't speak if muted - check at the very start using ref for immediate check
    if (isMutedRef.current || isMuted) {
      return Promise.resolve();
    }

    const lang = language || navigator.language || 'en-US';

    // Stop any currently playing audio IMMEDIATELY
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      } catch (_e) {
        // Ignore errors when stopping
      }
    }

    // Cancel any pending TTS generation (only if no abortSignal - caller manages cancellation when signal provided)
    if (!abortSignal && ttsGenerationAbortRef.current) {
      ttsGenerationAbortRef.current.abort();
      ttsGenerationAbortRef.current = null;
    }

    // Mark that we're generating TTS
    isGeneratingTTSRef.current = true;

    // Use provided abort signal or create new abort controller
    let signalToUse: AbortSignal;
    let abortController: AbortController | null = null;

    if (abortSignal) {
      // Use the provided signal directly - caller (useEffect) manages the controller
      signalToUse = abortSignal;
    } else {
      // Create new abort controller
      abortController = new AbortController();
      signalToUse = abortController.signal;
      ttsGenerationAbortRef.current = abortController;
    }

    // Generate and play TTS with abort signal support
    return textToSpeech(text, lang, signalToUse)
      .then(audioBlob => {
        // Check if this generation was aborted
        if (signalToUse.aborted) {
          // Clean up the blob URL
          const tempUrl = URL.createObjectURL(audioBlob);
          URL.revokeObjectURL(tempUrl);
          isGeneratingTTSRef.current = false;
          return;
        }

        // Check mute state again before playing (user might have muted while loading)
        if (isMutedRef.current || isMuted) {
          URL.revokeObjectURL(URL.createObjectURL(audioBlob));
          isGeneratingTTSRef.current = false;
          return;
        }

        // Stop any audio that might have started playing while we were generating
        if (currentAudioRef.current) {
          try {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
          } catch (e) {
            // Ignore errors
          }
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        currentAudioRef.current = audio;

        // Play audio
        audio.play().catch(error => {
          console.error('Error playing TTS audio:', error);
          currentAudioRef.current = null;
          URL.revokeObjectURL(audioUrl);
          isGeneratingTTSRef.current = false;
        });

        // Clean up the URL after playing
        audio.addEventListener('ended', () => {
          URL.revokeObjectURL(audioUrl);
          currentAudioRef.current = null;
          isGeneratingTTSRef.current = false;
        }, { once: true });

        // Clean up on error
        audio.addEventListener('error', () => {
          URL.revokeObjectURL(audioUrl);
          currentAudioRef.current = null;
          isGeneratingTTSRef.current = false;
        }, { once: true });
      })
      .catch(error => {
        // Don't log if aborted (this is expected behavior)
        if (error.name === 'AbortError' || signalToUse.aborted) {
          // This is expected - the request was cancelled
          isGeneratingTTSRef.current = false;
          return;
        }
        // Log other errors
        console.error('Error generating TTS:', error);
        isGeneratingTTSRef.current = false;
      })
      .finally(() => {
        // Clear abort controller if this was the current one (and we created it)
        if (abortController && ttsGenerationAbortRef.current === abortController) {
          ttsGenerationAbortRef.current = null;
        }
      });
  }, [isMuted]);

  // Auto-speak only the last assistant message (force stop any previous playback)
  useEffect(() => {
    if (messages.length === 0) return;
    const lastAssistantMessage = [...messages].reverse().find(msg => msg.role === Role.ASSISTANT);
    if (
      lastAssistantMessage &&
      lastAssistantMessage.id !== lastSpokenMessageIdRef.current &&
      !isMuted
    ) {
      // Update the last spoken ID immediately to prevent duplicate calls
      lastSpokenMessageIdRef.current = lastAssistantMessage.id;

      // Force stop any currently playing audio IMMEDIATELY
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        } catch (_e) {
          // Ignore errors when stopping
        }
      }

      // Cancel any pending TTS generation
      if (ttsGenerationAbortRef.current) {
        ttsGenerationAbortRef.current.abort();
        ttsGenerationAbortRef.current = null;
      }
      isGeneratingTTSRef.current = false;

      // Clean the text (remove emojis, etc.) and speak
      const cleanText = lastAssistantMessage.text.replace(/🗺️/g, '').trim();
      if (cleanText) {
        // Create abort controller for this specific TTS call
        const abortController = new AbortController();
        ttsGenerationAbortRef.current = abortController;

        // Call speakMessage with the abort signal
        speakMessage(cleanText, navigator.language || 'en-US', abortController.signal).catch(err => {
          // Ignore abort errors
          if (err.name !== 'AbortError') {
            console.error('TTS error:', err);
          }
        });
      }
    }
  }, [messages, isMuted, speakMessage]);

  // Toggle mute state
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      isMutedRef.current = newMuted; // Update ref immediately

      // Stop any currently playing audio when muting
      if (newMuted) {
        // Muting - stop any playing audio immediately
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        }
      }
      // If unmuting, audio will play on next speakMessage call
      return newMuted;
    });
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true; // Keep listening continuously, don't stop on silence
        recognition.interimResults = true;
        recognition.lang = navigator.language || 'en-US';

        recognition.onstart = () => {
          setIsListening(true);
        };

        recognition.onend = () => {
          // Only stop if button is released, otherwise restart recognition
          if (isButtonHeldRef.current) {
            // Button is still held, restart recognition to continue listening
            try {
              recognition.start();
            } catch (error) {
              // Recognition might already be starting, ignore error
              console.log('Recognition restart:', error);
            }
          } else {
            // Button was released, stop listening
            setIsListening(false);
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          isButtonHeldRef.current = false; // Reset button held state on error
          if (event.error === 'not-allowed') {
            alert('Microphone access denied. Please enable microphone permissions.');
          }
        };

        recognition.onresult = (event: any) => {
          // Don't update input if a message was just sent (prevents text from reappearing)
          if (isMessageSentRef.current) {
            return;
          }

          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          // Accumulate final transcripts across continuous recognition events
          if (finalTranscript.trim()) {
            accumulatedTranscriptRef.current += finalTranscript;
          }

          // Show accumulated final text + current interim results
          const displayText = accumulatedTranscriptRef.current.trim() + (interimTranscript ? ' ' + interimTranscript : '');
          if (displayText.trim()) {
            setInputText(displayText.trim());
          }
        };

        recognitionRef.current = recognition;
      }
    }
  }, []);

  // Start voice input (green = idle, red = recording)
  const startVoiceInput = useCallback(() => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    // Stop agent TTS immediately so the user can talk (same as muting)
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      } catch (e) {
        // Ignore errors when stopping
      }
    }
    if (ttsGenerationAbortRef.current) {
      ttsGenerationAbortRef.current.abort();
      ttsGenerationAbortRef.current = null;
    }
    isGeneratingTTSRef.current = false;

    try {
      isButtonHeldRef.current = true; // Mark button as held
      accumulatedTranscriptRef.current = ''; // Reset accumulated transcript for new recording
      setIsListening(true); // Turn mic red immediately so user sees recording state
      setInputText('');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setIsListening(false);
      isButtonHeldRef.current = false;
    }
  }, []);

  // Stop voice input (release mic → stop recording, text stays in input)
  const stopVoiceInput = useCallback(() => {
    isButtonHeldRef.current = false; // Mark button as released
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
      setIsListening(false); // Turn mic green again

      // Ensure the final transcript is set in inputText
      if (accumulatedTranscriptRef.current.trim()) {
        setInputText(accumulatedTranscriptRef.current.trim());
      }

      // Focus the input and position cursor at the end of the text after a short delay
      setTimeout(() => {
        if (inputRef.current && isChatOpen) {
          inputRef.current.focus();
          // Position cursor at the end of the text
          const textLength = inputRef.current.value.length;
          inputRef.current.setSelectionRange(textLength, textLength);
        }
      }, 100);
    }
  }, [isChatOpen]);

  // Add welcome message when chat opens if no start/end locations are set
  useEffect(() => {
    if (isChatOpen && messages.length === 0 && !startLocation && !endLocation) {
      setMessages([
        {
          id: 'welcome',
          role: Role.ASSISTANT,
          text: WELCOME_MESSAGE,
          timestamp: Date.now()
        }
      ]);
      // TTS will be handled by the centralized useEffect watching messages
    }
  }, [isChatOpen, startLocation, endLocation, messages.length]);


  // Helper function to get fastest route for comparison (simple route without stops)
  const getFastestRoute = useCallback(async (start: string, end: string) => {
    if (!start || !end) return null;

    try {
      const travelMode = tripConfig?.travelMode || 'DRIVING';
      const fastestRouteResponse = await getRoute({
        start,
        end,
        mode: travelMode,
        preferences: '', // No preferences = fastest route
        alternative: false
      });

      if (fastestRouteResponse.polyline && fastestRouteResponse.legs) {
        const totalTimeMinutes = fastestRouteResponse.legs.reduce(
          (sum, leg) => sum + (leg.duration?.value || 0),
          0
        ) / 60;

        const fastestRouteSuggestion: RouteSuggestion = {
          name: 'Fastest Route',
          stops: [],
          suggested_stops: [],
          num_stops: 0,
          total_time: totalTimeMinutes,
          polyline: fastestRouteResponse.polyline,
          legs: fastestRouteResponse.legs
        };
        return fastestRouteSuggestion;
      }
    } catch (error) {
      console.error('Failed to fetch fastest route:', error);
    }
    return null;
  }, [tripConfig, sessionId]);


  // Handle location selection from map click or input
  const handleLocationSelect = useCallback(async (
    location: Location,
    address: string,
    type: 'start' | 'end'
  ) => {
    // Prevent changes when chat is open
    if (isChatOpen) {
      return;
    }

    setIsSelectingLocation(null);

    // Update state
    let newStart = startLocation;
    let newEnd = endLocation;

    if (type === 'start') {
      setStartLocation(address);
      setStartLocationCoords(location);
      newStart = address;

      // Automatically focus on end location input after start is selected
      setTimeout(() => {
        if (endLocationInputRef.current && !isChatOpen) {
          endLocationInputRef.current.focus();
          setIsSelectingLocation('end');
        }
      }, 100);
    } else {
      setEndLocation(address);
      setEndLocationCoords(location);
      newEnd = address;
    }

    // Update map center to selected location
    setMapCenter(location);

    // Auto-fetch fastest route and open chat with greeting when end location is selected
    if (type === 'end' && newStart && newEnd) {
      // Remove welcome message if it exists when locations are selected
      setMessages(prev => prev.filter(msg => msg.id !== 'welcome'));

      // Fetch the fastest route first (before showing chat)
      const fetchFastestRouteAndShowChat = async () => {
        try {
          // Get fastest route
          const travelMode = tripConfig?.travelMode || 'DRIVING';
          const fastestRouteResponse = await getRoute({
            start: newStart,
            end: newEnd,
            mode: travelMode,
            preferences: '', // No preferences = fastest route
            alternative: false
          });

          if (fastestRouteResponse.polyline && fastestRouteResponse.legs) {
            // Calculate total time for the fastest route
            const totalTimeMinutes = fastestRouteResponse.legs.reduce(
              (sum, leg) => sum + (leg.duration?.value || 0),
              0
            ) / 60;

            // Create fastest route suggestion object
            const fastestRouteSuggestion: RouteSuggestion = {
              name: 'Fastest Route',
              stops: [],
              suggested_stops: [],
              num_stops: 0,
              total_time: totalTimeMinutes,
              polyline: fastestRouteResponse.polyline,
              legs: fastestRouteResponse.legs
            };

            // Store fastest route and set it as active
            setFastestRoute(fastestRouteSuggestion);
            setRoutePolyline(fastestRouteResponse.polyline);
            setRouteSuggestions([]); // Clear any previous suggestions
            setActiveRouteSuggestion(-1); // Set to -1 to indicate fastest route is selected
            setSuggestedPlaces([]); // Clear any previous suggested places

            // Set start/end coords from legs so "Go!" and "Start Journey" work
            if (fastestRouteResponse.legs?.length) {
              const firstLeg = fastestRouteResponse.legs[0];
              const lastLeg = fastestRouteResponse.legs[fastestRouteResponse.legs.length - 1];
              if (firstLeg?.start_location) setStartLocationCoords(firstLeg.start_location);
              if (lastLeg?.end_location) setEndLocationCoords(lastLeg.end_location);
            }

            console.log('Fastest route displayed on map');
          }
        } catch (error) {
          console.error('Failed to fetch fastest route:', error);
          // Continue to show chat even if route fetch fails
        }

        // Check if we've already shown greeting for this route combination
        const routeKey = `${newStart}|${newEnd}`;
        const hasShownGreeting = messages.some(
          msg => msg.id === `route-greeting-${routeKey}`
        );

        if (!hasShownGreeting) {
          // Generate greeting message based on language
          const langCode = (navigator.language || 'en-US').split('-')[0];
          let greetingMessage = '';

          if (langCode === 'fa' || langCode === 'prs') {
            greetingMessage = `👋 سلام! من دستیار RouteX شما هستم. خوشحالم که می‌توانم به شما کمک کنم تا مسیر از ${newStart} به ${newEnd} را برنامه‌ریزی کنید. چگونه می‌توانم کمکتان کنم؟\n\nبا من صحبت کنید یا علایق خود را در چت تایپ کنید...`;
          } else {
            greetingMessage = `👋 Hi! I'm your RouteX assistant. I'm happy to help you plan your route from ${newStart} to ${newEnd}. How can I assist you today?\n\nTalk with me or type in your interests...`;
          }

          const greetingMsg: Message = {
            id: `route-greeting-${routeKey}`,
            role: Role.ASSISTANT,
            text: greetingMessage,
            timestamp: Date.now()
          };

          setMessages(prev => [...prev, greetingMsg]);

          // Open chat to show the greeting
          setIsChatOpen(true);

          // Speak the greeting message
          speakMessage(greetingMessage, navigator.language || 'en-US');
        } else {
          // If greeting already shown, just open chat
          setIsChatOpen(true);
        }
      };

      // Fetch route and show chat
      fetchFastestRouteAndShowChat();
    }
  }, [startLocation, endLocation, messages, speakMessage]);

  // Handle location input change (text input)
  const handleLocationInputChange = useCallback((value: string, type: 'start' | 'end') => {
    // Prevent changes when chat is open
    if (isChatOpen) {
      return;
    }
    if (type === 'start') {
      setStartLocation(value);
    } else {
      setEndLocation(value);
    }
    // Don't auto-plan route - user must click the "Plan Route" button
  }, [isChatOpen]);

  // Handle map click for location selection (address already geocoded by GoogleMap)
  const handleMapClick = useCallback((location: Location, address?: string) => {
    // Prevent map clicks from updating addresses when chat is open
    if (isChatOpen) {
      return;
    }
    if (isSelectingLocation) {
      const finalAddress = address || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
      handleLocationSelect(location, finalAddress, isSelectingLocation);
    }
  }, [isSelectingLocation, handleLocationSelect, isChatOpen]);

  // Parse route from text (e.g., "from X to Y")
  const parseRouteFromText = (text: string): { start: string; end: string } | null => {
    // Clean up common prefixes first
    let cleanText = text
      .replace(/^(?:plan\s+)?(?:a\s+)?(?:route|trip|journey|drive)\s+/i, '')
      .trim();

    // Pattern: "from X to Y"
    const fromToPattern = /^from\s+(.+?)\s+to\s+(.+?)(?:\s+with|\s+stopping|\s+via|\s*$)/i;
    let match = cleanText.match(fromToPattern);

    if (match && match[1] && match[2]) {
      return {
        start: match[1].trim(),
        end: match[2].trim()
      };
    }

    // Alternative pattern: "X to Y" (without "from")
    const simplePattern = /^(.+?)\s+to\s+(.+?)(?:\s+with|\s+stopping|\s+via|\s*$)/i;
    match = cleanText.match(simplePattern);

    if (match && match[1] && match[2]) {
      // Make sure start doesn't contain route-related words
      const start = match[1].trim();
      if (!start.match(/^(?:plan|route|trip|journey|drive)$/i)) {
        return {
          start: start,
          end: match[2].trim()
        };
      }
    }

    return null;
  };

  // Handle sending a message. When skipUserMessage is true, the user message is not shown in chat (e.g. filter clicks).
  const handleSendMessage = async (textOverride?: string, skipUserMessage?: boolean) => {
    const text = textOverride || inputText;
    if (!text.trim() || isLoading) return;

    // Initialize account if needed
    if (user?.uid) {
      getOrInitializeAccount(user.uid);

      // Check if account can use the app
      if (!canUseApp(user.uid)) {
        setShowUpgradePrompt(true);
        setIsChatOpen(true);
        const upgradeMsg: Message = {
          id: Date.now().toString(),
          role: Role.ASSISTANT,
          text: "⚠️ You've used all your free responses! Please upgrade to Professional or Premium to continue using RouteX.",
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, upgradeMsg]);
        return;
      }
    }

    if (!skipUserMessage) {
      const userMsg: Message = {
        id: Date.now().toString(),
        role: Role.USER,
        text: text,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, userMsg]);
    }
    // Clear input text and voice transcript immediately when sending
    if (!textOverride) {
      setInputText('');
      accumulatedTranscriptRef.current = ''; // Clear voice transcript to prevent it from reappearing
      isMessageSentRef.current = true; // Mark that a message was sent to prevent onresult from restoring text
    }
    setIsLoading(true);

    try {
      const response = await sendChatMessage({
        message: text,
        session_id: sessionId,
        start: startLocation,
        end: endLocation,
        current_preferences: selectedFilters.join(','),
        language: navigator.language || 'en-US',
        travel_mode: tripConfig?.travelMode,
        radius: tripConfig?.radius,
        max_time: tripConfig?.maxTime,
        number_of_people: tripConfig?.numberOfPeople
      });

      // Check if response contains route suggestions (from agent)
      if (response.suggestions && response.suggestions.length > 0) {
        console.log('Chat response includes route suggestions:', response.suggestions.length, 'suggestions');

        // Find the suggestion with the maximum number of stops (start visualization from that route)
        const getStopCount = (s: typeof response.suggestions[0]) =>
          s.num_stops ?? (s.stops?.length ?? (s.suggested_stops?.length ?? 0));
        let maxStopsIndex = 0;
        let maxStops = getStopCount(response.suggestions[0]);
        for (let i = 1; i < response.suggestions.length; i++) {
          const count = getStopCount(response.suggestions[i]);
          if (count > maxStops) {
            maxStops = count;
            maxStopsIndex = i;
          }
        }

        setRouteSuggestions(response.suggestions);
        setActiveRouteSuggestion(maxStopsIndex);

        // Fetch the fastest route (without stops) for comparison
        if (startLocation && endLocation) {
          const fastest = await getFastestRoute(startLocation, endLocation);
          setFastestRoute(fastest);
        }

        // Set polyline and places from the route with max stops
        const selectedSuggestion = response.suggestions[maxStopsIndex];
        setRoutePolyline(selectedSuggestion.polyline);

        // Set start/end coords from route legs so "Go!" and "Start Journey" work when routes are suggested
        if (selectedSuggestion.legs?.length) {
          const firstLeg = selectedSuggestion.legs[0];
          const lastLeg = selectedSuggestion.legs[selectedSuggestion.legs.length - 1];
          if (firstLeg?.start_location) setStartLocationCoords(firstLeg.start_location);
          if (lastLeg?.end_location) setEndLocationCoords(lastLeg.end_location);
        }

        const stops = selectedSuggestion.stops || selectedSuggestion.suggested_stops;
        const numStops = selectedSuggestion.num_stops || stops?.length || 0;

        if (stops && stops.length > 0) {
          const placesFromStops: Place[] = stops.map((stop, index) => {
            if ('place_id' in stop && 'name' in stop && 'location' in stop) {
              return stop as Place;
            }
            const latLngStop = stop as {
              lat: number;
              lng: number;
              name?: string;
              place_id?: string;
              type?: string;
              types?: string[];
              found_for_preference?: string;
              rating?: number;
              formatted_address?: string;
            };
            return {
              place_id: latLngStop.place_id || `stop-${index}`,
              name: latLngStop.name || `Stop ${index + 1}`,
              location: { lat: latLngStop.lat, lng: latLngStop.lng },
              types: latLngStop.types || (latLngStop.type ? [latLngStop.type] : undefined),
              found_for_preference: latLngStop.found_for_preference,
              rating: latLngStop.rating,
              formatted_address: latLngStop.formatted_address,
            };
          });
          setSuggestedPlaces(placesFromStops);
        }

        // Add assistant message with route info (refer to selected route with max stops)
        const routeMsgText = response.reasoning
          ? `🗺️ I found ${response.suggestions.length} route option(s) for you!\n\n${response.reasoning}\n\nThe route with most stops has ${numStops} stops and takes about ${Math.round(selectedSuggestion.total_time)} minutes.`
          : `🗺️ I found ${response.suggestions.length} route option(s) for you! The route with most stops has ${numStops} stops and takes about ${Math.round(selectedSuggestion.total_time)} minutes.`;

        const routeMsg: Message = {
          id: (Date.now() + 1).toString(),
          role: Role.ASSISTANT,
          text: routeMsgText,
          timestamp: Date.now()
        };

        if (user?.uid && routeMsgText.trim()) {
          decrementResponseCount(user.uid);
        }

        setMessages(prev => [...prev, routeMsg]);
        // TTS will be handled by the centralized useEffect watching messages
        return; // Exit early since we handled route suggestions
      }

      // Update filters if preferences were extracted
      let allPreferences = [...selectedFilters];
      if (response.preferences && response.preferences.length > 0) {
        allPreferences = [...new Set([...selectedFilters, ...response.preferences])];
        setSelectedFilters(allPreferences);
      }

      const botMsgText = response.response || "I'm here to help you plan your journey!";
      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: Role.ASSISTANT,
        text: botMsgText,
        timestamp: Date.now()
      };

      // Decrement response count when assistant message is received
      if (user?.uid && botMsgText.trim()) {
        decrementResponseCount(user.uid);
      }

      setMessages(prev => [...prev, botMsg]);
      // TTS will be handled by the centralized useEffect watching messages

      // Check if this is a route request and update locations if needed
      const routeInfo = parseRouteFromText(text);
      if (routeInfo && !isChatOpen) {
        console.log('Detected route request:', routeInfo);
        // Remove welcome message if it exists when route is detected
        setMessages(prev => prev.filter(msg => msg.id !== 'welcome'));
        // Update start/end locations
        setStartLocation(routeInfo.start);
        setEndLocation(routeInfo.end);
        // Agent will handle route planning through the chat response
      }

      // If new preferences were extracted and we already have start/end, ask agent to plan route
      // But only if we don't already have route suggestions from this response
      if (response.preferences && response.preferences.length > 0 && startLocation && endLocation && !response.suggestions) {
        console.log('New preferences extracted, asking agent to plan route with:', allPreferences);
        // Send a follow-up message to agent to plan route with preferences
        const preferenceText = allPreferences.join(', ');
        // Use setTimeout to avoid calling handleSendMessage recursively
        setTimeout(() => {
          handleSendMessage(`Plan route from ${startLocation} to ${endLocation} with ${preferenceText}`);
        }, 100);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsgText = "Sorry, I encountered an error. Please try again.";
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: Role.ASSISTANT,
        text: errorMsgText,
        timestamp: Date.now()
      };

      // Decrement response count when assistant error message is received
      if (user?.uid && errorMsgText.trim()) {
        decrementResponseCount(user.uid);
      }

      setMessages(prev => [...prev, errorMsg]);
      // TTS will be handled by the centralized useEffect watching messages
    } finally {
      setIsLoading(false);
      // Clear input text and voice transcript after message is sent (ready for next conversation)
      if (!textOverride) {
        setInputText('');
        accumulatedTranscriptRef.current = ''; // Clear voice transcript to prevent it from reappearing
      }
      // Reset the flag after a delay to allow onresult events to finish processing
      setTimeout(() => {
        isMessageSentRef.current = false;
      }, 500);
      // Ensure focus stays on chat input after message is sent
      setTimeout(() => {
        if (inputRef.current && isChatOpen) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  // Handle form submission
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up
    handleSendMessage();
    // Keep focus on chat input after sending
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Handle filter chip click - wait 2 sec before sending to assistant (debounce)
  const handleFilterClick = (preference: string) => {
    // Check if account can use the app
    if (user?.uid && !canUseApp(user.uid)) {
      setShowUpgradePrompt(true);
      setIsChatOpen(true);
      const upgradeMsg: Message = {
        id: Date.now().toString(),
        role: Role.ASSISTANT,
        text: "⚠️ You've used all your free responses! Please upgrade to Professional or Premium to continue using RouteX.",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, upgradeMsg]);
      return;
    }

    const filter = DEFAULT_FILTERS.find(f => f.preference === preference);
    if (!filter) return;

    if (filterSendMessageTimeoutRef.current) {
      clearTimeout(filterSendMessageTimeoutRef.current);
    }
    filterSendMessageTimeoutRef.current = setTimeout(() => {
      filterSendMessageTimeoutRef.current = null;
      const selected = selectedFiltersRef.current;
      if (selected.length === 0) return;
      const labels = selected
        .map(p => DEFAULT_FILTERS.find(f => f.preference === p)?.label?.toLowerCase())
        .filter(Boolean);
      if (labels.length > 0) {
        handleSendMessage(`Find nearby ${labels.join(', ')}`, true); // true = do not show this text in chat
        setIsChatOpen(true);
      }
    }, 2000);
  };

  // Handle filter toggle - wait 2 sec before sending to assistant (debounce), then ask agent to plan route
  const handleFilterToggle = useCallback((preference: string, selected: boolean) => {
    setSelectedFilters(prev => {
      const newFilters = selected
        ? [...prev, preference]
        : prev.filter(p => p !== preference);

      // Wait 2 sec before asking agent to plan route (if both locations are set)
      if (startLocation && endLocation) {
        if (filterPlanRouteTimeoutRef.current) {
          clearTimeout(filterPlanRouteTimeoutRef.current);
        }
        filterPlanRouteTimeoutRef.current = setTimeout(() => {
          filterPlanRouteTimeoutRef.current = null;
          const selected = selectedFiltersRef.current;
          if (selected.length > 0) {
            const labels = selected
              .map(p => DEFAULT_FILTERS.find(f => f.preference === p)?.label?.toLowerCase())
              .filter(Boolean);
            if (labels.length > 0) {
              handleSendMessage(`Plan route from ${startLocation} to ${endLocation} with ${labels.join(', ')}`);
            }
          }
        }, 2000);
      }

      return newFilters;
    });
  }, [startLocation, endLocation, handleSendMessage]);

  // Handle place selection
  const handlePlaceSelect = (placeId: string) => {
    const place = suggestedPlaces.find(p => p.place_id === placeId);
    if (place) {
      handleSendMessage(`Add ${place.name} as a stop on my route`);
    }
  };

  // Handle place click on map - show details modal
  const handlePlaceClick = (place: Place) => {
    setSelectedPlace(place);
    setIsPlaceDetailsOpen(true);
  };

  // Recalculate route with updated waypoints
  const recalculateRouteWithWaypoints = useCallback(async (places: Place[], currentRouteSuggestions: RouteSuggestion[], currentActiveIndex: number) => {
    if (!startLocation || !endLocation || !startLocationCoords || !endLocationCoords) {
      console.log('Missing start/end locations for route recalculation');
      return;
    }

    // Only recalculate if we have route suggestions (not for simple routes)
    if (currentRouteSuggestions.length === 0 || currentActiveIndex < 0) {
      console.log('No route suggestions or invalid active suggestion index');
      return;
    }

    console.log('Recalculating route with', places.length, 'waypoints');

    try {
      const travelMode = tripConfig?.travelMode || 'DRIVING';
      const waypoints = places.map((place) => ({
        lat: place.location.lat,
        lng: place.location.lng,
      }));
      const recalculatedRoute = await recalculateRoute({
        start: startLocation,
        end: endLocation,
        mode: travelMode,
        waypoints,
      });

      if (!recalculatedRoute.polyline || !recalculatedRoute.legs) {
        console.error('Route recalculation returned incomplete data');
        return;
      }

      const polyline = recalculatedRoute.polyline;
      const formattedLegs = recalculatedRoute.legs;
      const totalTimeMinutes = formattedLegs.reduce(
        (sum, leg) => sum + (leg.duration?.value || 0),
        0
      ) / 60;

      console.log('Route recalculated successfully. Polyline length:', polyline.length);
      console.log('Total time:', totalTimeMinutes, 'minutes');
      console.log('Number of stops:', places.length);

      // Update the active route suggestion if we have one
      if (currentRouteSuggestions.length > 0 && currentActiveIndex >= 0) {
        // First clear the polyline to force map update
        setRoutePolyline(undefined);

        // Then update route suggestions
        setRouteSuggestions(prev => {
          const updated = [...prev];
          if (updated[currentActiveIndex]) {
            updated[currentActiveIndex] = {
              ...updated[currentActiveIndex],
              polyline: polyline,
              legs: formattedLegs,
              total_time: totalTimeMinutes,
              num_stops: places.length,
              stops: places,
              suggested_stops: places.map(p => ({
                lat: p.location.lat,
                lng: p.location.lng,
                name: p.name,
                place_id: p.place_id,
                type: p.types?.[0] || p.found_for_preference || 'place'
              }))
            };
            console.log('Updated route suggestion at index', currentActiveIndex, 'with', places.length, 'stops and', totalTimeMinutes, 'minutes');
          }
          return updated;
        });

        // Update the route polyline after a brief delay to ensure state updates
        setTimeout(() => {
          setRoutePolyline(polyline);
          console.log('Set route polyline to map');
        }, 50);
      } else {
        // If no route suggestions, just update the polyline directly
        setRoutePolyline(undefined);
        setTimeout(() => {
          setRoutePolyline(polyline);
        }, 50);
      }
    } catch (error) {
      console.error('Error recalculating route:', error);
    }
  }, [startLocation, endLocation, startLocationCoords, endLocationCoords, tripConfig]);

  // Remove place from route
  const handleRemovePlace = useCallback((placeId: string) => {
    setSuggestedPlaces((prev) => {
      const updated = prev.filter((p) => p.place_id !== placeId);
      // Recalculate route with updated places
      if (updated.length !== prev.length) {
        // Get current state values to pass to recalculation
        setRouteSuggestions(currentSuggestions => {
          setTimeout(() => {
            recalculateRouteWithWaypoints(updated, currentSuggestions, activeRouteSuggestionRef.current);
          }, 100);
          return currentSuggestions; // Return unchanged for now, will be updated by recalculateRouteWithWaypoints
        });
      }
      return updated;
    });
  }, [recalculateRouteWithWaypoints]);

  // Reorder stops in Directions tab (called by @dnd-kit onDragEnd)
  const handleDirectionsDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = suggestedPlaces.map((p, i) => p.place_id || `stop-${i}`);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(suggestedPlaces, oldIndex, newIndex);
      reorderedStopsRef.current = reordered;
      setSuggestedPlaces(reordered);
      setRouteSuggestions((current) => {
        if (reorderedStopsRef.current) {
          setTimeout(() => {
            recalculateRouteWithWaypoints(
              reorderedStopsRef.current!,
              current,
              activeRouteSuggestionRef.current
            );
            reorderedStopsRef.current = null;
          }, 100);
        }
        return current;
      });
    },
    [suggestedPlaces, recalculateRouteWithWaypoints]
  );

  // Build Google Maps navigation URL for the selected route (shared for Directions tab and map Start button)
  const buildGoogleMapsUrl = useCallback((): string => {
    if (!startLocationCoords || !endLocationCoords) return '';
    const origin = `${startLocationCoords.lat},${startLocationCoords.lng}`;
    const destination = `${endLocationCoords.lat},${endLocationCoords.lng}`;
    let url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}`;
    if (suggestedPlaces.length > 0) {
      const waypoints = suggestedPlaces.map(p => `${p.location.lat},${p.location.lng}`).join('|');
      url += `&waypoints=${encodeURIComponent(waypoints)}`;
    }
    return url;
  }, [startLocationCoords, endLocationCoords, suggestedPlaces]);

  const buildAppleMapsUrl = useCallback((): string => {
    const fallback = 'https://maps.apple.com/';
    // Apple parses human-readable addresses for multi-stop chains more reliably than raw lat/lng.
    const s =
      startLocation ||
      (startLocationCoords
        ? `${startLocationCoords.lat},${startLocationCoords.lng}`
        : '');
    const finalDestination =
      endLocation ||
      (endLocationCoords
        ? `${endLocationCoords.lat},${endLocationCoords.lng}`
        : '');
    if (!s && !finalDestination) return fallback;

    const stopSegments = suggestedPlaces.map((p) => {
      return (
        p.formatted_address ||
        p.name ||
        `${p.location.lat},${p.location.lng}`
      );
    });
    // Apple Maps supports daddr chains with "to:" separators.
    const routeSegments = [...stopSegments, finalDestination].filter(Boolean);
    const routeChainEncoded = routeSegments
      .map((segment) => encodeURIComponent(segment))
      .join('%20to:%20');

    const query: string[] = [];
    if (s) query.push(`saddr=${encodeURIComponent(s)}`);
    if (routeChainEncoded) query.push(`daddr=${routeChainEncoded}`);
    const mode = tripConfig?.travelMode || 'DRIVING';
    query.push(
      `dirflg=${mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd'}`
    );
    return `${fallback}?${query.join('&')}`;
  }, [
    startLocationCoords,
    endLocationCoords,
    startLocation,
    endLocation,
    tripConfig,
    suggestedPlaces,
  ]);

  const getRoutePointsForApple = useCallback((): string[] => {
    const start =
      startLocation ||
      (startLocationCoords
        ? `${startLocationCoords.lat},${startLocationCoords.lng}`
        : '');
    const stops = suggestedPlaces.map((p) => {
      return (
        p.formatted_address ||
        p.name ||
        `${p.location.lat},${p.location.lng}`
      );
    });
    const destination =
      endLocation ||
      (endLocationCoords
        ? `${endLocationCoords.lat},${endLocationCoords.lng}`
        : '');
    return [start, ...stops, destination].filter(Boolean);
  }, [
    startLocation,
    startLocationCoords,
    suggestedPlaces,
    endLocation,
    endLocationCoords,
  ]);

  const buildAppleLegUrl = useCallback(
    (fromPoint: string, toPoint: string): string => {
      const mode = tripConfig?.travelMode || 'DRIVING';
      const params = new URLSearchParams();
      params.set('saddr', fromPoint);
      params.set('daddr', toPoint);
      params.set('dirflg', mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd');
      return `https://maps.apple.com/?${params.toString()}`;
    },
    [tripConfig]
  );

  const handleStartJourney = useCallback((app: 'google' | 'apple' = 'google') => {
    if (app === 'apple') {
      const points = getRoutePointsForApple();
      if (points.length >= 2) {
        // Start leg-by-leg Apple navigation to ensure all stops are covered.
        setAppleLegPoints(points);
        setCurrentAppleLegIndex(0);
        const firstLegUrl = buildAppleLegUrl(points[0], points[1]);
        window.open(firstLegUrl, '_blank');
        return;
      }
      // Fallback for edge cases
      const fallback = buildAppleMapsUrl();
      if (fallback) window.open(fallback, '_blank');
      return;
    }
    const mapsUrl = buildGoogleMapsUrl();
    if (mapsUrl) window.open(mapsUrl, '_blank');
  }, [
    buildGoogleMapsUrl,
    buildAppleMapsUrl,
    getRoutePointsForApple,
    buildAppleLegUrl,
  ]);

  const handleGoClick = useCallback(() => {
    setShowGoConfirmDialog(true);
  }, []);

  const handleGoConfirmYes = useCallback(() => {
    setShowGoConfirmDialog(false);
    setShowBookingPlaceholder(true);
  }, []);

  const handleGoConfirmNo = useCallback(() => {
    setShowGoConfirmDialog(false);
    setShowNavigationAppDialog(true);
  }, []);

  const handleSaveJourney = useCallback(() => {
    if (!user?.uid || !startLocationCoords || !endLocationCoords) return;
    saveSavedJourney(user.uid, {
      startAddress: startLocation || '',
      endAddress: endLocation || '',
      startCoords: startLocationCoords,
      endCoords: endLocationCoords,
      places: suggestedPlaces,
      routePolyline,
      routeSuggestions: routeSuggestions?.length ? routeSuggestions : undefined,
    });
    setSavedJourneyMessage('Journey saved!');
    setTimeout(() => setSavedJourneyMessage(null), 3000);
  }, [user?.uid, startLocationCoords, endLocationCoords, startLocation, endLocation, suggestedPlaces, routePolyline, routeSuggestions]);

  const selectFastestRouteOption = useCallback(() => {
    if (!fastestRoute) return;
    setActiveRouteSuggestion(-1);
    setRoutePolyline(fastestRoute.polyline);
    setSuggestedPlaces([]); // No stops for fastest route
  }, [fastestRoute]);

  const selectSuggestedRouteOption = useCallback((index: number) => {
    const suggestion = routeSuggestions[index];
    if (!suggestion) return;
    setActiveRouteSuggestion(index);
    setRoutePolyline(suggestion.polyline);

    const stops = suggestion.stops || suggestion.suggested_stops;
    if (stops && stops.length > 0) {
      const places: Place[] = stops.map((stop, idx) => {
        if ('place_id' in stop && 'name' in stop && 'location' in stop) {
          return stop as Place;
        }
        const latLngStop = stop as {
          lat: number;
          lng: number;
          name?: string;
          place_id?: string;
          type?: string;
          types?: string[];
          found_for_preference?: string;
          rating?: number;
          formatted_address?: string;
        };
        return {
          place_id: latLngStop.place_id || `stop-${idx}`,
          name: latLngStop.name || `Stop ${idx + 1}`,
          location: { lat: latLngStop.lat, lng: latLngStop.lng },
          types: latLngStop.types || (latLngStop.type ? [latLngStop.type] : undefined),
          found_for_preference: latLngStop.found_for_preference,
          rating: latLngStop.rating,
          formatted_address: latLngStop.formatted_address,
        };
      });
      setSuggestedPlaces(places);
    } else {
      setSuggestedPlaces([]);
    }
  }, [routeSuggestions]);

  const handleLoadSavedJourney = useCallback((journey: SavedJourney) => {
    setStartLocation(journey.startAddress);
    setEndLocation(journey.endAddress);
    setStartLocationCoords(journey.startCoords);
    setEndLocationCoords(journey.endCoords);
    setSuggestedPlaces(journey.places);
    setRoutePolyline(journey.routePolyline ?? undefined);
    setRouteSuggestions(journey.routeSuggestions ?? []);
    setActiveTab('route');
  }, []);

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'route':
        // Helper function to get readable place type
        const getPlaceType = (place: Place): string => {
          // First priority: use found_for_preference if available
          if (place.found_for_preference) {
            // Capitalize first letter
            return place.found_for_preference.charAt(0).toUpperCase() + place.found_for_preference.slice(1);
          }

          // Second priority: use types array
          if (place.types && place.types.length > 0) {
            // Filter out generic types and get the most specific one
            const specificTypes = place.types.filter(type =>
              !type.includes('establishment') &&
              !type.includes('point_of_interest') &&
              type !== 'geocode' &&
              type !== 'route' &&
              type !== 'locality' &&
              type !== 'political'
            );
            if (specificTypes.length > 0) {
              // Capitalize and format the type
              const formattedType = specificTypes[0].split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
              return formattedType;
            }
            // If no specific types, use the first type anyway (better than "Place")
            const formattedType = place.types[0].split('_').map(word =>
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
            return formattedType;
          }

          return 'Place';
        };

        const formatTime = (d: Date): string => {
          try {
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } catch {
            return '';
          }
        };

        const formatMinutesFromSeconds = (sec: number | undefined): string => {
          const s = sec ?? 0;
          const min = Math.max(0, Math.round(s / 60));
          return `${min} min`;
        };

        const activeLegs =
          activeRouteSuggestion === -1
            ? fastestRoute?.legs
            : routeSuggestions[activeRouteSuggestion]?.legs;

        const startTime =
          directionsStartTimeMode === 'custom' && directionsStartDateTime
            ? new Date(directionsStartDateTime)
            : new Date();

        const estimateStayMinutesForPlaceType = (placeTypeStr: string, placeName: string): number => {
          const t = (placeTypeStr || '').toLowerCase();
          const n = (placeName || '').toLowerCase();

          // Museums / attractions
          if (t.includes('museum') || t.includes('gallery') || t.includes('attraction')) return 90;
          if (t.includes('park') || t.includes('garden') || t.includes('zoo')) return 75;
          if (t.includes('theater') || t.includes('cinema') || t.includes('movie')) return 120;

          // Food & drink
          if (t.includes('restaurant') || t.includes('diner')) return 75;
          if (t.includes('cafe') || t.includes('coffee')) return 40;
          if (t.includes('bar') || t.includes('pub')) return 60;

          // Fitness / errands
          if (t.includes('gym') || t.includes('fitness') || t.includes('yoga')) return 60;
          if (t.includes('supermarket') || t.includes('grocery')) return 30;
          if (t.includes('shopping')) return 75;
          if (t.includes('pharmacy')) return 15;
          if (t.includes('gas') || t.includes('fuel')) return 10;
          if (t.includes('bank') || t.includes('atm')) return 10;

          // Heuristics from name
          if (n.includes('museum')) return 90;
          if (n.includes('park')) return 75;
          if (n.includes('cafe') || n.includes('coffee')) return 40;
          if (n.includes('restaurant')) return 75;

          // Default
          return 45;
        };

        // Build ETA info for each stop from legs durations (leg 0 = start -> stop1, ...)
        const etaByStopId: Record<
          string,
          { etaText?: string; estStayText?: string; estStayMinutes?: number }
        > = {};
        if (suggestedPlaces.length > 0 && activeLegs && activeLegs.length > 0) {
          let cumulativeTravelSeconds = 0;
          let cumulativeStaySeconds = 0;
          suggestedPlaces.forEach((p, idx) => {
            const leg = activeLegs[idx]; // stop idx corresponds to leg idx from start
            const legSeconds = leg?.duration?.value ?? 0;
            const stopId = p.place_id || `stop-${idx}`;
            const defaultEstMin = estimateStayMinutesForPlaceType(getPlaceType(p), p.name);
            const override = stayOverridesByStopId[stopId];
            const estMin =
              override != null && Number.isFinite(override) ? Math.max(0, override) : defaultEstMin;

            // ETA for this stop = start time + all travel up to this stop + all stays at previous stops
            cumulativeTravelSeconds += legSeconds;
            const etaSeconds = cumulativeTravelSeconds + cumulativeStaySeconds;
            const eta = new Date(startTime.getTime() + etaSeconds * 1000);

            // After arriving here, assume you spend estMin here before moving on to the next stop
            cumulativeStaySeconds += Math.max(0, Math.round(estMin * 60));

            etaByStopId[stopId] = {
              etaText: formatTime(eta),
              estStayMinutes: estMin,
              estStayText: estMin ? `~${Math.round(estMin / 5) * 5} min` : undefined,
            };
          });
        }

        return (
          <div className="p-4 pb-24">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Directions</h2>
            {(fastestRoute || routeSuggestions.length > 0) && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">{routeSuggestions.length > 1 ? 'Route options:' : 'Your route:'}</p>
                <div className="flex gap-2 overflow-x-auto">
                  {fastestRoute && (
                    <button
                      type="button"
                      onClick={selectFastestRouteOption}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeRouteSuggestion === -1 ? 'bg-black text-white' : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      Fastest
                      <span className="block text-xs opacity-75">
                        0 stops • {Math.round(fastestRoute.total_time)} min
                      </span>
                    </button>
                  )}
                  {routeSuggestions.map((suggestion, index) => {
                    const stops = suggestion.stops || suggestion.suggested_stops;
                    const numStops = suggestion.num_stops || stops?.length || 0;
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => selectSuggestedRouteOption(index)}
                        className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeRouteSuggestion === index ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Route {index + 1}
                        <span className="block text-xs opacity-75">
                          {numStops} stops • {Math.round(suggestion.total_time)} min
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {suggestedPlaces.length > 0 && (
              <p className="text-sm text-gray-500 mb-4">Drag stops to change their order.</p>
            )}
            {suggestedPlaces.length === 0 && <div className="mb-4" />}
            <div className="space-y-3">
              {/* Start Location */}
              {startLocation && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-green-600 text-[24px] mt-0.5">
                      place
                    </span>
                    <div className="flex-1 min-w-0 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-500 mb-1">Start</p>
                        <p className="text-gray-900">{startLocation}</p>
                      </div>

                      {/* Start time picker on the right side of Start address */}
                      <div className="flex-shrink-0 text-right">
                        <p className="text-xs font-semibold text-gray-600 mb-1">
                          Start time
                        </p>
                        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setDirectionsStartTimeMode('now')}
                            className={`px-3 py-1.5 text-sm font-semibold ${
                              directionsStartTimeMode === 'now'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Now
                          </button>
                          <button
                            type="button"
                            onClick={() => setDirectionsStartTimeMode('custom')}
                            className={`px-3 py-1.5 text-sm font-semibold ${
                              directionsStartTimeMode === 'custom'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Choose
                          </button>
                        </div>

                        {directionsStartTimeMode === 'custom' && (
                          <input
                            type="datetime-local"
                            value={directionsStartDateTime}
                            onChange={(e) => setDirectionsStartDateTime(e.target.value)}
                            className="mt-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stops (draggable to reorder via @dnd-kit) */}
              <DndContext
                sensors={directionsSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDirectionsDragEnd}
              >
                <SortableContext
                  items={suggestedPlaces.map((p, i) => p.place_id || `stop-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {suggestedPlaces.map((place, index) => (
                      <SortableStopRow
                        key={place.place_id || `stop-${index}`}
                        id={place.place_id || `stop-${index}`}
                        place={place}
                        displayIndex={index}
                        placeType={getPlaceType(place)}
                        etaText={etaByStopId[place.place_id || `stop-${index}`]?.etaText}
                        estStayMinutes={etaByStopId[place.place_id || `stop-${index}`]?.estStayMinutes}
                        estStayText={etaByStopId[place.place_id || `stop-${index}`]?.estStayText}
                        onStayMinutesChange={(minutes) => {
                          const stopId = place.place_id || `stop-${index}`;
                          setStayOverridesByStopId((prev) => ({ ...prev, [stopId]: minutes }));
                        }}
                        onPlaceClick={handlePlaceClick}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {/* End Location */}
              {endLocation && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-red-600 text-[24px] mt-0.5">
                      place
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500 mb-1">Destination</p>
                      <p className="text-gray-900">{endLocation}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!startLocation && !endLocation && suggestedPlaces.length === 0 && (
                <div className="text-center py-12">
                  <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">
                    directions
                  </span>
                  <p className="text-gray-500">No route planned yet</p>
                  <p className="text-sm text-gray-400 mt-2">Plan a route to see directions here</p>
                </div>
              )}

              {/* Start Journey + Save Journey buttons - show when route is suggested or start/end coords set */}
              {(routePolyline || (routeSuggestions && routeSuggestions.length > 0) || (startLocationCoords && endLocationCoords)) && (
                <div className="fixed bottom-20 left-0 right-0 px-4 pb-safe-bottom z-30 space-y-2">
                  <button
                    onClick={handleGoClick}
                    className="w-full bg-blue-600 text-white rounded-xl py-4 font-semibold shadow-lg flex items-center justify-center gap-2 active:bg-blue-700 transition-colors"
                  >
                    <span className="material-symbols-outlined">navigation</span>
                    Start Journey
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveJourney}
                    className="w-full bg-white border-2 border-gray-200 text-gray-800 rounded-xl py-3 font-medium shadow flex items-center justify-center gap-2 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[20px]">bookmark</span>
                    Save Journey for Future
                  </button>
                  {savedJourneyMessage && (
                    <p className="text-center text-sm text-green-600 font-medium">{savedJourneyMessage}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'contribute':
        return (
          <div className="p-4 pb-24">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Contribute</h2>
            <p className="text-sm text-gray-500 mb-6">Help improve places on Google Maps.</p>
            <div className="space-y-3">
              <a
                href="https://www.google.com/maps/contrib"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full p-4 rounded-xl border-2 border-gray-200 text-gray-800 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[28px] text-green-600">add_location</span>
                <div>
                  <p className="font-semibold text-gray-900">Add place</p>
                  <p className="text-sm text-gray-500">Add a missing place to the map</p>
                </div>
                <span className="material-symbols-outlined text-gray-400 ml-auto">open_in_new</span>
              </a>
              <a
                href="https://www.google.com/maps"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full p-4 rounded-xl border-2 border-gray-200 text-gray-800 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[28px] text-amber-600">rate_review</span>
                <div>
                  <p className="font-semibold text-gray-900">Add review</p>
                  <p className="text-sm text-gray-500">Share your experience at a place</p>
                </div>
                <span className="material-symbols-outlined text-gray-400 ml-auto">open_in_new</span>
              </a>
              <a
                href="https://www.google.com/maps"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full p-4 rounded-xl border-2 border-gray-200 text-gray-800 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[28px] text-blue-600">add_a_photo</span>
                <div>
                  <p className="font-semibold text-gray-900">Add photo with score</p>
                  <p className="text-sm text-gray-500">Upload a photo and rate a place</p>
                </div>
                <span className="material-symbols-outlined text-gray-400 ml-auto">open_in_new</span>
              </a>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings</h2>

            {/* Profile Section */}
            <ProfileSection onLoadSavedJourney={handleLoadSavedJourney} />

            {/* Trip Configuration */}
            {tripConfig && (
              <div className="mt-6 space-y-2">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Trip Configuration</h3>
                <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
                  {/* Trip Type */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">today</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Trip Type</p>
                        <p className="text-sm text-gray-500">
                          {tripType === 'single-day' ? 'Single Day Trip' :
                           tripType === 'multiple-days' ? 'Multiple Days Trip' :
                           tripType === 'deep-planning' ? 'Deep Planning' : 'Not selected'}
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={onBackToWelcome} className="text-blue-600 font-medium text-sm hover:text-blue-700">
                      Edit
                    </button>
                  </div>

                  {/* Travel Mode */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">
                        {tripConfig.travelMode === 'DRIVING' ? 'directions_car' :
                         tripConfig.travelMode === 'BICYCLING' ? 'directions_bike' :
                         tripConfig.travelMode === 'WALKING' ? 'directions_walk' :
                         'directions_transit'}
                      </span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Travel Mode</p>
                        <p className="text-sm text-gray-500">
                          {tripConfig.travelMode === 'DRIVING' ? 'Car' :
                           tripConfig.travelMode === 'BICYCLING' ? 'Bike' :
                           tripConfig.travelMode === 'WALKING' ? 'Walk' :
                           'Public Transport'}
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={onEditConfig} className="text-blue-600 font-medium text-sm hover:text-blue-700">
                      Edit
                    </button>
                  </div>

                  {/* Search Radius */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">location_searching</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Search Radius</p>
                        <p className="text-sm text-gray-500">{tripConfig.radius} km</p>
                      </div>
                    </div>
                    <button type="button" onClick={onEditConfig} className="text-blue-600 font-medium text-sm hover:text-blue-700">
                      Edit
                    </button>
                  </div>

                  {/* Maximum Travel Time */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">schedule</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Maximum Travel Time</p>
                        <p className="text-sm text-gray-500">
                          {tripConfig.maxTime < 60
                            ? `${tripConfig.maxTime} min`
                            : tripConfig.maxTime === 60
                            ? '1 hour'
                            : tripConfig.maxTime % 60 === 0
                            ? `${tripConfig.maxTime / 60} hours`
                            : `${Math.floor(tripConfig.maxTime / 60)}h ${tripConfig.maxTime % 60}m`}
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={onEditConfig} className="text-blue-600 font-medium text-sm hover:text-blue-700">
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* App Settings */}
            <div className="mt-6 space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">App Settings</h3>
              <div className="flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Location Services</p>
                  <p className="text-sm text-gray-500">
                    {location ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <span className={`material-symbols-outlined ${location ? 'text-green-600' : 'text-gray-400'}`}>
                  {location ? 'check_circle' : 'cancel'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Language</p>
                  <p className="text-sm text-gray-500">{navigator.language || 'English'}</p>
                </div>
                <span className="material-symbols-outlined text-gray-400">edit</span>
              </div>
              <p className="text-xs text-gray-400 pt-4">RouteX AI version 1.0.0</p>
            </div>
          </div>
        );

      default: // map
        return null;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden relative font-sans">

      {/* Map layer - always mounted but hidden when not active to preserve state */}
      <div className={`absolute inset-0 z-10 ${activeTab === 'map' ? '' : 'invisible'}`}>
        <GoogleMap
          center={mapCenter}
          zoom={14}
          places={suggestedPlaces}
           polyline={
             startLocationCoords && endLocationCoords
               ? correctedRoutePolyline
               : routePolyline
           }
          fastestRoutePolyline={
            fastestRoute && routeSuggestions.length > 0 && activeRouteSuggestion !== -1
              ? fastestRoute.polyline
              : undefined
          }
          currentLocation={location ? { lat: location.latitude, lng: location.longitude } : undefined}
          startLocation={startLocationCoords}
          endLocation={endLocationCoords}
          isSelectingLocation={isSelectingLocation}
          onMapClick={handleMapClick}
          hasPreferences={routeSuggestions.length > 0 && activeRouteSuggestion >= 0}
          travelMode={tripConfig?.travelMode ?? 'DRIVING'}
          className="w-full h-full"
          onPlaceClick={handlePlaceClick}
        />

        {/* Only show map overlays when map tab is active */}
        {activeTab === 'map' && (
          <>
          {/* Block map interaction and show Planning... when chat closed and agent working */}
          {!isChatOpen && isLoading && (
            <>
              <div
                className="absolute inset-0 z-20 bg-black/10 pointer-events-auto cursor-wait flex items-center justify-center"
                aria-hidden
              >
                <div className="bg-white/95 backdrop-blur rounded-xl shadow-lg border border-gray-200 px-5 py-3 flex items-center gap-2">
                  <span
                    className="rounded-full border-2 border-blue-600 border-t-transparent animate-spin flex-shrink-0"
                    style={{ width: 22, height: 22 }}
                  />
                  <span className="text-gray-800 font-medium text-base">Planning...</span>
                </div>
              </div>
            </>
          )}
          {/* Location inputs panel - Google Maps style */}
          <div className="absolute top-16 left-4 right-4 z-25">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300">
              {/* Collapse/Expand button */}
              <button
                onClick={() => setIsLocationPanelCollapsed(!isLocationPanelCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100"
              >
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-gray-600 text-[20px]">
                    {isLocationPanelCollapsed ? 'expand_more' : 'expand_less'}
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    {isLocationPanelCollapsed ? 'Show Route Details' : 'Hide Route Details'}
                  </span>
                </div>
                {startLocation && endLocation && (
                  <span className="text-xs text-gray-500">
                    {startLocation.split(',')[0]} → {endLocation.split(',')[0]}
                  </span>
                )}
              </button>

              {/* Collapsible content */}
              <div className={`transition-all duration-300 ${
                isLocationPanelCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100'
              }`}>
                <div className="p-3 space-y-2">
              {/* Start location input */}
              <LocationInput
                value={startLocation}
                onChange={(value) => handleLocationInputChange(value, 'start')}
                onLocationSelect={(location, address) => handleLocationSelect(location, address, 'start')}
                placeholder="Choose starting point"
                icon="place"
                iconColor="#34C759"
                isStart={true}
                disabled={isChatOpen}
                onFocus={() => {
                  if (!isChatOpen) {
                    setIsSelectingLocation('start');
                  }
                }}
                onBlur={() => {
                  // Delay to allow click events to fire
                  setTimeout(() => setIsSelectingLocation(null), 200);
                }}
              />

              {/* End location input */}
              <LocationInput
                value={endLocation}
                onChange={(value) => handleLocationInputChange(value, 'end')}
                onLocationSelect={(location, address) => handleLocationSelect(location, address, 'end')}
                placeholder="Choose destination"
                icon="place"
                iconColor="#FF3B30"
                isStart={false}
                inputRef={endLocationInputRef}
                disabled={isChatOpen}
                onFocus={() => {
                  if (!isChatOpen) {
                    setIsSelectingLocation('end');
                  }
                }}
                onBlur={() => {
                  // Delay to allow click events to fire
                  setTimeout(() => setIsSelectingLocation(null), 200);
                }}
              />

                  {/* Selection hint */}
                  {isSelectingLocation && (
                    <div className="text-xs text-blue-600 px-2 py-1 bg-blue-50 rounded-lg">
                      Click on the map to select {isSelectingLocation === 'start' ? 'starting' : 'end'} point
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


          {/* Route suggestions panel - show when we have any suggestions */}
          {routeSuggestions.length >= 1 && (
            <div className={`absolute left-4 right-4 z-25 transition-all duration-300 ${
              isLocationPanelCollapsed
                ? 'top-[108px]' // Right below collapsed location panel button (top-16=64px + button ~44px = 108px) with tiny gap
                : 'top-56' // Below location panel when expanded (224px)
            }`}>
              <div className="bg-white rounded-xl shadow-lg p-3">
                <p className="text-sm text-gray-500 mb-2">{routeSuggestions.length > 1 ? 'Route options:' : 'Your route:'}</p>
                <div className="flex gap-2 overflow-x-auto">
                  {/* Show fastest route first if available */}
                  {fastestRoute && (
                    <button
                      key="fastest"
                      onClick={selectFastestRouteOption}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeRouteSuggestion === -1
                          ? 'bg-black text-white'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      Fastest
                      <span className="block text-xs opacity-75">
                        0 stops • {Math.round(fastestRoute.total_time)} min
                      </span>
                    </button>
                  )}
                  {routeSuggestions.map((suggestion, index) => {
                    const stops = suggestion.stops || suggestion.suggested_stops;
                    const numStops = suggestion.num_stops || stops?.length || 0;
                    return (
                      <button
                        key={index}
                        onClick={() => selectSuggestedRouteOption(index)}
                        className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeRouteSuggestion === index
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Route {index + 1}
                        <span className="block text-xs opacity-75">
                          {numStops} stops • {Math.round(suggestion.total_time)} min
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Go! button - show when route is suggested (polyline or suggestions) or when start/end coords set */}
          {(routePolyline || (routeSuggestions && routeSuggestions.length > 0) || (startLocationCoords && endLocationCoords)) && (
            <button
              onClick={handleGoClick}
              className="absolute bottom-40 right-4 bg-blue-600 text-white rounded-full px-4 py-3 shadow-lg z-20 hover:bg-blue-700 active:bg-blue-800 transition-colors font-semibold text-sm"
              title="Start Journey"
            >
              Go!
            </button>
          )}

          {/* Floating location button */}
          {location && (
            <button
              onClick={() => setMapCenter({ lat: location.latitude, lng: location.longitude })}
              className="absolute bottom-24 right-4 bg-white rounded-full p-3 shadow-lg z-20"
            >
              <span className="material-symbols-outlined text-blue-600">my_location</span>
            </button>
          )}
          </>
        )}
      </div>

      {/* Top floating layer - Filter chips only (for map tab) */}
      {activeTab === 'map' && (
        <div className="absolute top-0 left-0 right-0 z-30 pt-safe-top bg-gradient-to-b from-white via-white/95 to-transparent pb-2">
          <div className="pt-2">
            <FilterChips
              filters={DEFAULT_FILTERS}
              selectedFilters={selectedFilters}
              onFilterClick={handleFilterClick}
              onFilterToggle={handleFilterToggle}
            />
          </div>
        </div>
      )}

      {/* Floating Chat Button (FAB) - draggable, only show when chat is closed and on map tab */}
      {activeTab === 'map' && !isChatOpen && (
        <button
          ref={chatButtonRef}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            const rect = chatButtonRef.current?.getBoundingClientRect();
            if (rect) {
              isDraggingChatButtonRef.current = false;
              setIsDraggingChatButton(false);
              setDragStartPos({
                x: touch.clientX - (rect.left + rect.width / 2),
                y: touch.clientY - (rect.top + rect.height / 2)
              });
            }
          }}
          onTouchMove={(e) => {
            // Only prevent default if we've moved enough to be considered dragging
            const touch = e.touches[0];
            const moveThreshold = 10; // pixels
            const moved = Math.abs(touch.clientX - dragStartPos.x) > moveThreshold ||
                         Math.abs(touch.clientY - dragStartPos.y) > moveThreshold;

            if (moved) {
              e.preventDefault();
              isDraggingChatButtonRef.current = true;
              setIsDraggingChatButton(true);
              const buttonSize = 56; // 14 * 4 = 56px (w-14 h-14)
              const bottomNavHeight = 80;
              const newX = Math.max(buttonSize / 2, Math.min(window.innerWidth - buttonSize / 2, touch.clientX - dragStartPos.x));
              const newY = Math.max(buttonSize / 2, Math.min(window.innerHeight - buttonSize / 2 - bottomNavHeight, touch.clientY - dragStartPos.y));
              setChatButtonPosition({
                x: newX - buttonSize / 2,
                y: window.innerHeight - newY - buttonSize / 2 - bottomNavHeight
              });
            }
          }}
          onTouchEnd={(e) => {
            const wasDragging = isDraggingChatButtonRef.current;
            isDraggingChatButtonRef.current = false;
            setIsDraggingChatButton(false);
            if (!wasDragging) {
              // If we weren't dragging, open chat
              e.preventDefault();
              setIsChatOpen(true);
            }
          }}
          onMouseDown={(e) => {
            const rect = chatButtonRef.current?.getBoundingClientRect();
            if (rect) {
              isDraggingChatButtonRef.current = false;
              setIsDraggingChatButton(false);
              setDragStartPos({
                x: e.clientX - (rect.left + rect.width / 2),
                y: e.clientY - (rect.top + rect.height / 2)
              });
            }

            const buttonSize = 56;
            const bottomNavHeight = 80;

            const handleMouseMove = (e: MouseEvent) => {
              isDraggingChatButtonRef.current = true;
              setIsDraggingChatButton(true);
              const newX = Math.max(buttonSize / 2, Math.min(window.innerWidth - buttonSize / 2, e.clientX - dragStartPos.x));
              const newY = Math.max(buttonSize / 2, Math.min(window.innerHeight - buttonSize / 2 - bottomNavHeight, e.clientY - dragStartPos.y));
              setChatButtonPosition({
                x: newX - buttonSize / 2,
                y: window.innerHeight - newY - buttonSize / 2 - bottomNavHeight
              });
            };

            const handleMouseUp = () => {
              const wasDragging = isDraggingChatButtonRef.current;
              isDraggingChatButtonRef.current = false;
              setIsDraggingChatButton(false);
              if (!wasDragging) {
                // If we weren't dragging, open chat
                setIsChatOpen(true);
              }
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
          onClick={(e) => {
            // Only open chat if we weren't dragging
            if (!isDraggingChatButtonRef.current) {
              e.preventDefault();
              e.stopPropagation();
              setIsChatOpen(true);
            }
          }}
          className="fixed w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg z-50 hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center cursor-pointer"
          style={{
            left: `${chatButtonPosition.x}px`,
            bottom: `${chatButtonPosition.y}px`,
            transition: isDraggingChatButton ? 'none' : 'left 0.2s, bottom 0.2s',
          }}
        >
          <span className="material-symbols-outlined text-[28px]">chat</span>
        </button>
      )}

      {/* Chat Modal/Panel */}
      {isChatOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 transition-opacity"
            onClick={() => setIsChatOpen(false)}
          />

          {/* Chat Panel */}
          <div
            className={`relative w-full max-w-lg bg-white rounded-t-3xl shadow-2xl animate-slide-up flex flex-col transition-all duration-300 ${
              isChatExpanded ? 'max-h-[95vh]' : 'max-h-[85vh]'
            }`}
          >
            {/* Swipe handle */}
            <div
              className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing"
              onTouchStart={(e) => {
                setChatSwipeStartY(e.touches[0].clientY);
              }}
              onTouchMove={(e) => {
                const currentY = e.touches[0].clientY;
                const diff = chatSwipeStartY - currentY;

                // Swipe up to expand (diff > 50)
                if (diff > 50 && !isChatExpanded) {
                  setIsChatExpanded(true);
                }
                // Swipe down to collapse (diff < -50)
                else if (diff < -50 && isChatExpanded) {
                  setIsChatExpanded(false);
                }
              }}
              onMouseDown={(e) => {
                setChatSwipeStartY(e.clientY);
              }}
              onMouseMove={(e) => {
                if (e.buttons === 1) { // Left mouse button is pressed
                  const currentY = e.clientY;
                  const diff = chatSwipeStartY - currentY;

                  if (diff > 50 && !isChatExpanded) {
                    setIsChatExpanded(true);
                  }
                  else if (diff < -50 && isChatExpanded) {
                    setIsChatExpanded(false);
                  }
                }
              }}
            >
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-600">smart_toy</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">RouteX Assistant</h3>
                  <p className="text-xs text-gray-500">AI Travel Planner</p>
                </div>
              </div>
              <button
                onClick={() => setIsChatOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <span className="material-symbols-outlined text-gray-500">close</span>
              </button>
            </div>

            {/* Chat messages */}
            <div className={`flex-1 overflow-y-auto px-4 py-3 custom-scrollbar min-h-[300px] transition-all duration-300 ${
              isChatExpanded ? 'max-h-[75vh]' : 'max-h-[50vh]'
            }`}>
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  onPlaceSelect={handlePlaceSelect}
                />
              ))}

              {/* Live draft: show current input text in the chat while typing */}
              {inputText.trim() && (
                <div className="flex justify-end mb-4 px-2 opacity-90">
                  <div className="max-w-[85%] bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-3">
                    <p className="text-[15px] leading-relaxed text-white whitespace-pre-wrap break-words">
                      {inputText}
                    </p>
                    <p className="text-[10px] mt-2 text-blue-100">Typing...</p>
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="mb-3 px-2 flex justify-start">
                  <AgentStatusBar message={LOADING_STATUS_MESSAGES[loadingStatusIndex]} />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Chat input */}
            <div className="px-4 py-3 border-t border-gray-100 bg-white rounded-b-3xl">
              <form onSubmit={handleFormSubmit} className="flex items-center gap-2">
                {/* Mute/Speaker button */}
                <button
                  type="button"
                  onClick={toggleMute}
                  className={`p-2 rounded-full transition-colors flex-shrink-0 ${
                    isMuted
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                  title={isMuted ? 'Unmute audio' : 'Mute audio'}
                >
                  <span className="material-symbols-outlined text-[20px] block">
                    {isMuted ? 'volume_off' : 'volume_up'}
                  </span>
                </button>

                {/* Voice input button */}
                <button
                  type="button"
                  onMouseDown={startVoiceInput}
                  onMouseUp={stopVoiceInput}
                  onMouseLeave={stopVoiceInput}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    startVoiceInput();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    stopVoiceInput();
                  }}
                  className={`p-2 rounded-full transition-colors flex-shrink-0 ${
                    isListening
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                  title="Hold to speak"
                >
                  <span className="material-symbols-outlined text-[20px] block">
                    mic
                  </span>
                </button>

                {/* Text input container */}
                <div className="relative flex-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                      // Prevent Enter key from bubbling to other inputs or triggering other forms
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        // Submit the form programmatically
                        handleFormSubmit(e as any);
                      } else {
                        // Stop propagation for all keys to prevent interference
                        e.stopPropagation();
                      }
                    }}
                    onKeyPress={(e) => {
                      // Additional protection - stop all keypress events from bubbling
                      e.stopPropagation();
                    }}
                    placeholder={
                      user?.uid && !canUseApp(user.uid)
                        ? "Upgrade to continue..."
                        : "Ask for directions, places..."
                    }
                    disabled={user?.uid ? !canUseApp(user.uid) : false}
                    className={`w-full bg-gray-100 text-gray-900 rounded-full py-3 pl-5 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500/50 border border-transparent transition-all placeholder-gray-500 text-[15px] ${
                      user?.uid && !canUseApp(user.uid) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    autoComplete="off"
                    data-form-type="chat"
                  />

                  {/* Send button */}
                  <button
                    type="submit"
                    disabled={!inputText.trim() || isLoading || (user?.uid ? !canUseApp(user.uid) : false)}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-colors ${
                      inputText.trim() && (user?.uid ? canUseApp(user.uid) : true)
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-transparent text-gray-400'
                    }`}
                  >
                    <span className="material-symbols-outlined text-[20px] block">send</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Main content layer - for non-map tabs */}
      {activeTab !== 'map' && (
        <main className="flex-1 bg-gray-50/50 overflow-y-auto custom-scrollbar pt-safe-top pb-[80px]">
          {renderTabContent()}
        </main>
      )}

      {/* Bottom navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200">
        <BottomNav
          tabs={DEFAULT_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Apple leg-by-leg navigator helper */}
      {appleLegPoints.length >= 2 && currentAppleLegIndex < appleLegPoints.length - 1 && (
        <div className="fixed left-4 right-4 bottom-24 z-[55]">
          <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Apple Maps navigation</p>
            <p className="text-sm font-medium text-gray-800 mb-2">
              Leg {currentAppleLegIndex + 1} of {appleLegPoints.length - 1}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => {
                  const fromPoint = appleLegPoints[currentAppleLegIndex];
                  const toPoint = appleLegPoints[currentAppleLegIndex + 1];
                  if (!fromPoint || !toPoint) return;
                  const mode = tripConfig?.travelMode || 'DRIVING';
                  const params = new URLSearchParams();
                  params.set('saddr', fromPoint);
                  params.set('daddr', toPoint);
                  params.set('dirflg', mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd');
                  window.open(`https://maps.apple.com/?${params.toString()}`, '_blank');
                }}
                className="flex-1 py-2.5 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Redo current step
              </button>
              <button
                onClick={() => {
                  const nextLegIndex = currentAppleLegIndex + 1;
                  if (nextLegIndex >= appleLegPoints.length - 1) {
                    setAppleLegPoints([]);
                    setCurrentAppleLegIndex(0);
                    return;
                  }
                  const fromPoint = appleLegPoints[nextLegIndex];
                  const toPoint = appleLegPoints[nextLegIndex + 1];
                  const mode = tripConfig?.travelMode || 'DRIVING';
                  const params = new URLSearchParams();
                  params.set('saddr', fromPoint);
                  params.set('daddr', toPoint);
                  params.set('dirflg', mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd');
                  window.open(`https://maps.apple.com/?${params.toString()}`, '_blank');
                  setCurrentAppleLegIndex(nextLegIndex);
                }}
                className="flex-1 py-2.5 px-3 bg-black text-white rounded-lg text-sm font-semibold hover:bg-gray-900"
              >
                Next stop in Apple Maps
              </button>
              <button
                onClick={() => {
                  setAppleLegPoints([]);
                  setCurrentAppleLegIndex(0);
                }}
                className="py-2.5 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                End
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Go! confirmation: "Book before journey?" */}
      {showGoConfirmDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowGoConfirmDialog(false)}
            aria-hidden
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <p className="text-gray-800 text-center mb-6">
              Do you want me to book (if possible) before starting your journey?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleGoConfirmYes}
                className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
              >
                Yes
              </button>
              <button
                onClick={handleGoConfirmNo}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 font-medium transition-colors"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking placeholder (future tools) */}
      {showBookingPlaceholder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowBookingPlaceholder(false)}
            aria-hidden
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <div className="text-center mb-6">
              <span className="material-symbols-outlined text-4xl text-blue-500 mb-3 block">event_available</span>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Book before journey</h2>
              <p className="text-gray-600 text-sm">
                Booking tools will be available here in a future update. You can continue and choose your navigation app now.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBookingPlaceholder(false)}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 font-medium transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowBookingPlaceholder(false);
                  setShowNavigationAppDialog(true);
                }}
                className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation app chooser */}
      {showNavigationAppDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowNavigationAppDialog(false)}
            aria-hidden
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <p className="text-gray-800 text-center mb-5 font-medium">
              Open your journey in which app?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setShowNavigationAppDialog(false);
                  handleStartJourney('google');
                }}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
              >
                Google Maps
              </button>
              <button
                onClick={() => {
                  setShowNavigationAppDialog(false);
                  handleStartJourney('apple');
                }}
                className="w-full py-3 px-4 bg-black text-white rounded-xl hover:bg-gray-900 font-medium transition-colors"
              >
                Apple Maps
              </button>
              <button
                onClick={() => setShowNavigationAppDialog(false)}
                className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && user?.uid && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowUpgradePrompt(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <span className="material-symbols-outlined text-white text-3xl">lock</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Upgrade Required</h2>
              <p className="text-gray-600">
                You've used all your free responses. Upgrade to continue using RouteX!
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {/* Professional Plan */}
              <button
                onClick={() => {
                  if (user?.uid) {
                    updateAccountType(user.uid, 'Professional');
                    setShowUpgradePrompt(false);
                    const successMsg: Message = {
                      id: Date.now().toString(),
                      role: Role.ASSISTANT,
                      text: "✅ Your account has been upgraded to Professional! You can now continue using RouteX.",
                      timestamp: Date.now()
                    };
                    setMessages(prev => [...prev, successMsg]);
                  }
                }}
                className="w-full p-4 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Professional</h3>
                    <p className="text-sm text-gray-600">Unlimited responses</p>
                  </div>
                  <span className="text-blue-600 font-semibold">Upgrade</span>
                </div>
              </button>

              {/* Premium Plan */}
              <button
                onClick={() => {
                  if (user?.uid) {
                    updateAccountType(user.uid, 'Premium');
                    setShowUpgradePrompt(false);
                    const successMsg: Message = {
                      id: Date.now().toString(),
                      role: Role.ASSISTANT,
                      text: "✅ Your account has been upgraded to Premium! You can now continue using RouteX.",
                      timestamp: Date.now()
                    };
                    setMessages(prev => [...prev, successMsg]);
                  }
                }}
                className="w-full p-4 bg-purple-50 border-2 border-purple-200 rounded-xl hover:bg-purple-100 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Premium</h3>
                    <p className="text-sm text-gray-600">Unlimited responses + Advanced features</p>
                  </div>
                  <span className="text-purple-600 font-semibold">Upgrade</span>
                </div>
              </button>
            </div>

            <button
              onClick={() => {
                setShowUpgradePrompt(false);
                onBackToWelcome();
              }}
              className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Place Details Modal */}
      <PlaceDetailsModal
        place={selectedPlace}
        isOpen={isPlaceDetailsOpen}
        onClose={() => {
          setIsPlaceDetailsOpen(false);
          setSelectedPlace(null);
        }}
        onRemove={handleRemovePlace}
      />
    </div>
  );
};

// Root App Component with AuthProvider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
